/**
 * Client for the AI chat feature.
 *
 * Requests go through the Django proxy at /api/v1/ai/ which forwards
 * to the evvy-ai private service. Auth is handled by Django JWT cookies.
 */

import {
  decodeLiveCard,
  decodeSavedCard,
  type CommerceCardBlock,
  type SavedCommerceCardBlock,
} from "@/services/commerceOffers";
import type {
  ProgramCardBlock,
  SavedProgramCardBlock,
} from "@/services/commerceProgramOffers";
import { BASE_URL } from "@/utils/axios";

const AI_PATH = `${BASE_URL}/api/v1/ai`;

interface SessionResponse {
  session_id: string;
  bot_id: string;
  created_at: string;
  message_count: number;
  metadata: Record<string, unknown> | null;
  title: string | null;
  updated_at: string | null;
}

interface HistoryMessage {
  role: "user" | "assistant";
  content: string;
  blocks?: ContentBlock[];
}

interface ResponseMetadata {
  policies_applied: string[];
  detected_intents: string[];
  citations: string[];
  trace_id: string | null;
  trace_url: string | null;
  model: string | null;
  latency_ms: number | null;
  tokens: { input: number; output: number } | null;
  escalate_to_ops: boolean;
  escalate_to_emergency: boolean;
  response_source: string | null;
  user_data_sections: string[];
  query_templates_used: string[];
  system_prompt_length: number | null;
}

interface ChatResponse {
  session_id: string;
  response: string;
  metadata: ResponseMetadata | null;
}

interface FeedbackPayload {
  trace_id: string;
  value: 0 | 1;
  comment?: string;
  session_id?: string;
}

interface SourceItem {
  url: string;
  title: string;
  description: string;
  favicon_url: string;
}

type ContentBlock =
  | { type: "text"; content: string }
  | {
      type: "product";
      name: string;
      description: string;
      price: string;
      image_url: string;
      link: string;
    }
  | {
      type: "cta";
      label: string;
      action: { type: "url" | "route"; value: string };
    }
  | { type: "confidence"; level: "high" | "medium" | "low" }
  | { type: "sources"; items: SourceItem[] }
  | CommerceCardBlock
  | SavedCommerceCardBlock
  | ProgramCardBlock
  | SavedProgramCardBlock;

function getHeaders(): HeadersInit {
  const headers: HeadersInit = { "Content-Type": "application/json" };
  const token = localStorage.getItem("accessToken");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

export async function createAISession(botId: string): Promise<SessionResponse> {
  const response = await fetch(`${AI_PATH}/sessions/`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify({ bot_id: botId }),
  });
  if (!response.ok) throw new Error("Failed to create AI session");
  return response.json();
}

export async function* streamAIMessage(
  sessionId: string,
  message: string,
  signal?: AbortSignal,
  context?: { viewing_test_hash: string | null; page: string }
): AsyncGenerator<
  | { type: "chunk"; data: string }
  | { type: "metadata"; data: ResponseMetadata }
  | { type: "block"; data: ContentBlock }
  | { type: "guardrail_blocked"; replacement?: string }
> {
  const response = await fetch(`${AI_PATH}/chat/stream`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify({
      session_id: sessionId,
      message,
      ...(context && { context }),
    }),
    signal,
  });

  if (!response.ok) throw new Error("Stream request failed");

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";
  let nextEventType: string | null = null;
  let completed = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        nextEventType = line.slice(7).trim();
        continue;
      }
      if (line === "") {
        nextEventType = null;
        continue;
      }
      if (line.startsWith("data: ")) {
        const data = line.slice(6).trim();
        if (data === "[DONE]") return;
        if (!data) continue;

        if (nextEventType === "metadata") {
          nextEventType = null;
          try {
            const metadata = JSON.parse(data) as ResponseMetadata;
            completed = true;
            yield {
              type: "metadata",
              data: metadata,
            };
          } catch {
            /* malformed metadata — ignore */
          }
        } else if (nextEventType === "guardrail_blocked") {
          nextEventType = null;
          try {
            const parsed = JSON.parse(data) as { replacement?: unknown };
            yield {
              type: "guardrail_blocked",
              replacement:
                typeof parsed.replacement === "string"
                  ? parsed.replacement
                  : undefined,
            };
          } catch {
            yield { type: "guardrail_blocked" };
          }
        } else if (nextEventType === "block") {
          nextEventType = null;
          try {
            const block = JSON.parse(data);
            // A malformed live card is dropped; other blocks pass through as before.
            if (block?.type !== "commerce_card") {
              yield { type: "block", data: block as ContentBlock };
            } else {
              const card = decodeLiveCard(block);
              if (card) yield { type: "block", data: card };
            }
          } catch {
            /* malformed block — ignore */
          }
        } else {
          nextEventType = null;
          try {
            const parsed = JSON.parse(data);
            yield {
              type: "chunk",
              data: typeof parsed === "string" ? parsed : data,
            };
          } catch {
            yield { type: "chunk", data };
          }
        }
      }
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const remaining = buffer.trim();
    if (remaining.startsWith("data: ")) {
      const data = remaining.slice(6).trim();
      if (data === "[DONE]") return;
    }
  }
  if (!completed) throw new Error("Stream ended before completion");
}

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  const response = await fetch(`${AI_PATH}/feedback/`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error("Failed to submit feedback");
}

export async function listAISessions(): Promise<SessionResponse[]> {
  const response = await fetch(`${AI_PATH}/sessions/list/`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to list sessions");
  const data = await response.json();
  return Array.isArray(data) ? data : (data.sessions ?? []);
}

export async function getSessionHistory(
  sessionId: string
): Promise<HistoryMessage[]> {
  const response = await fetch(`${AI_PATH}/sessions/${sessionId}/history/`, {
    method: "GET",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to get session history");
  const data = await response.json();
  const messages: (HistoryMessage & { metadata?: unknown })[] = Array.isArray(
    data
  )
    ? data
    : (data.messages ?? []);
  return messages.map(({ role, content, metadata }) => ({
    role,
    content,
    blocks: decodeHistoryBlocks(metadata, content),
  }));
}

/** A saved answer's blocks, or undefined to show its text when the snapshot is unknown or malformed. */
function decodeHistoryBlocks(
  metadata: unknown,
  content: string
): ContentBlock[] | undefined {
  const rendering = (
    metadata as { rendering?: { schema_version?: unknown; blocks?: unknown } }
  )?.rendering;
  if (rendering?.schema_version !== 1 || !Array.isArray(rendering.blocks))
    return undefined;
  const blocks: ContentBlock[] = [];
  for (const saved of rendering.blocks as (ContentBlock | null)[]) {
    // Never the live card: history strips the action, and U7 refreshes by offer_id.
    const block =
      saved?.type === "commerce_card" ? decodeSavedCard(saved) : saved;
    // Shapes a renderer would throw on.
    if (
      !block ||
      (block.type === "text" && typeof block.content !== "string") ||
      (block.type === "sources" && !Array.isArray(block.items))
    )
      return undefined;
    const last = blocks[blocks.length - 1];
    // Saved one block per streamed chunk: merge them, as the live stream does.
    if (block.type === "text" && last?.type === "text")
      blocks[blocks.length - 1] = {
        type: "text",
        content: last.content + block.content,
      };
    else blocks.push(block);
  }
  // Blocks replace the text, so they must carry all of it.
  const text = blocks
    .map((block) => (block.type === "text" ? block.content : ""))
    .join("");
  if (text !== content) return undefined;
  // Saved buttons are hidden in past conversations; filtered after the merge so the
  // text blocks around one stay exactly as they were.
  return blocks.filter((block) => block.type !== "cta");
}

export async function acceptAIConsent(): Promise<void> {
  const response = await fetch(`${AI_PATH}/consent/`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!response.ok) throw new Error("Failed to accept AI consent");
}

export async function deleteAISession(sessionId: string): Promise<void> {
  await fetch(`${AI_PATH}/sessions/${sessionId}/`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
}

export type {
  ResponseMetadata,
  FeedbackPayload,
  SessionResponse,
  ChatResponse,
  ContentBlock,
  SourceItem,
  HistoryMessage,
};
